import React from 'react';
import { Plus, Package, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import { ViewLayout, Button } from '../ui';
import { supabase } from '../../supabaseClient';

const InventoryView = ({
  props,
  currentUser,
  openAddProp,
  openEditProp,
  setSelectedProp,
  onReturnProps,
  onDataChange // Add callback for data refresh
}) => {
  const { toast } = useToast();
  // Filter props to only show ones owned by current user's organization
  const userOrgProps = props.filter(prop => prop.owner === currentUser.organization);

  // Debug: Check what React is loading
  const remoteDebug = userOrgProps.find(p => p.name.includes('Remote'));
  if (remoteDebug) {
    console.log('TV Remote in React state:', remoteDebug);
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleReleaseProp = async (propId) => {
    if (!window.confirm('Release this prop from its reservation? This will make it available again.')) {
      return;
    }
    
    try {
      // Find the prop to get its reservation_id
      const prop = userOrgProps.find(p => p.id === propId);
      let reservationId = prop?.reservation_id;
      if (!reservationId) {
        const { data: reservationItem, error: reservationItemError } = await supabase
          .from('reservation_items')
          .select('reservation_id')
          .eq('prop_id', propId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (reservationItemError) {
          console.error('Error fetching reservation for prop release:', reservationItemError);
        } else {
          reservationId = reservationItem?.reservation_id;
        }
      }

      if (!reservationId) {
        toast.warning('No reservation found for this prop');
        return;
      }

      console.log('🔄 Releasing prop from reservation:', reservationId);
      
      const { error } = await supabase
        .from('props')
        .update({ 
          status: 'available',
          reservation_id: null,
          return_date: null
        })
        .eq('id', propId);
      
      if (error) throw error;
      
      console.log('✅ Prop released successfully');
      
      // Check if this was the last prop in the reservation
      const { data: remainingProps, error: checkError } = await supabase
        .from('props')
        .select('id')
        .eq('reservation_id', reservationId)
        .neq('id', propId); // Exclude the prop we just released
      
      if (checkError) {
        console.warn('⚠️ Could not check remaining props in reservation:', checkError);
      } else {
        console.log('📋 Remaining props in reservation:', remainingProps?.length || 0);
        
        // If no props remain in the reservation, cancel it
        if (!remainingProps || remainingProps.length === 0) {
          console.log('🚫 No props left in reservation - cancelling reservation');
          
          const { error: cancelError } = await supabase
            .from('reservations')
            .update({ 
              status: 'cancelled',
              admin_notes: 'Cancelled - all props released by admin'
            })
            .eq('id', reservationId);
          
          if (cancelError) {
            console.warn('⚠️ Could not cancel empty reservation:', cancelError);
          } else {
            console.log('✅ Empty reservation cancelled successfully');
          }
        }
      }
      
      toast.success('Prop released successfully!');
      
      // Use discrete refresh instead of hard reload
      if (onDataChange) {
        console.log('🔄 Refreshing data after prop release...');
        onDataChange();
      } else {
        // Fallback to hard refresh if no callback provided
        window.location.reload();
      }
    } catch (err) {
      toast.error('Failed to release prop', { title: 'Release Error' });
    }
  };

  const getStatusStyle = (status) => {
    if (status === 'available') return 'bg-green-100 text-green-800';
    if (status === 'checked_out') return 'bg-orange-100 text-orange-800';
    if (status === 'reserved') return 'bg-purple-100 text-purple-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusText = (status) => {
    if (status === 'available') return 'Available';
    if (status === 'checked_out') return 'Checked Out';
    if (status === 'reserved') return 'Reserved';
    return 'Needs Repair';
  };

  return (
    <ViewLayout
      title="Inventory Management"
      subtitle={`Managing props for ${currentUser.organization}`}
      actions={[
        <Button
          key="refresh"
          onClick={handleRefresh}
          variant="secondary"
          size="compact"
          icon={RefreshCw}
        >
          Refresh
        </Button>,
        <Button
          key="add"
          onClick={openAddProp}
          variant="primary"
          size="compact"
          icon={Plus}
        >
          Add New Prop
        </Button>
      ]}
      stats={[
        <ViewLayout.Stat
          key="total"
          title="Total Props"
          value={userOrgProps.length}
          icon={Package}
          color="blue"
        />,
        <ViewLayout.Stat
          key="available"
          title="Available"
          value={userOrgProps.filter(p => p.status === 'available').length}
          icon={CheckCircle}
          color="green"
        />,
        <ViewLayout.Stat
          key="checked_out"
          title="Checked Out"
          value={userOrgProps.filter(p => p.status === 'checked_out').length}
          icon={Clock}
          color="orange"
        />
      ]}
    >

      {/* Props Management Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Your Organization's Props</h3>
        </div>
        <div className="overflow-x-auto">
          {userOrgProps.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-medium">Name</th>
                  <th className="text-left p-4 font-medium">Category</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Condition</th>
                  <th className="text-left p-4 font-medium">Times Used</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {userOrgProps.map(prop => (
                  <tr key={prop.id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div className="font-medium">{prop.name}</div>
                      <div className="text-sm text-gray-600">{prop.barcode}</div>
                    </td>
                    <td className="p-4 text-sm">{prop.category}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(prop.status)}`}>
                        {getStatusText(prop.status)}
                      </span>
                    </td>
                    <td className="p-4 text-sm">{prop.condition}</td>
                    <td className="p-4 text-sm">{prop.checkout_count || 0}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditProp(prop)}
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setSelectedProp(prop)}
                          className="text-gray-600 hover:text-gray-700 text-xs"
                        >
                          View
                        </button>
                        {prop.status === 'checked_out' && (
                          <button
                            onClick={() => onReturnProps([prop.id])}
                            className="text-green-600 hover:text-green-700 text-xs font-medium"
                          >
                            Return
                          </button>
                        )}
                        {prop.status === 'reserved' && (
                          <button
                            onClick={() => handleReleaseProp(prop.id)}
                            className="text-orange-600 hover:text-orange-700 text-xs font-medium"
                          >
                            Release
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="mb-4">No props found for your organization</p>
              <button
                onClick={openAddProp}
                className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
              >
                Add Your First Prop
              </button>
            </div>
          )}
        </div>
      </div>
    </ViewLayout>
  );
};

export default InventoryView;
